import type { Locale } from "@porsche-customer/slfinrtl-context-types"
import type { Environment } from "@porsche-customer/slfinrtl-environment"
import type { AddOfferQueryParams } from "./buildAddOfferUrl"

export interface ConfiguratorModelSelectionParams {
	backActionUrl?: string
	canReportSales: boolean
	dealership: string
	environment: Environment | null
	locale: Locale
}

interface BuildConfiguratorModelSelectionUrlOptions {
	getOfferAddPath?: (query: AddOfferQueryParams) => string
	origin: string
}

interface Action {
	label: string
	url: string
}

function getConfiguratorHost(environment: Environment | null): string {
	return environment === "prod"
		? "https://configurator.porsche.com"
		: environment === "preview"
			? "https://configurator-staging.porsche.com"
			: "https://configurator-dev.porsche.com"
}

export function buildConfiguratorModelSelectionUrl(
	params: ConfiguratorModelSelectionParams,
	options: BuildConfiguratorModelSelectionUrlOptions,
): string {
	const { backActionUrl, canReportSales, dealership, environment, locale } = params

	const { getOfferAddPath, origin } = options

	// We have to use a placeholder here for the porscheCode because it will be replaced with the actual
	// porscheCode in the returnUrl by the configurator.
	const porscheCodePlaceholder = "{porscheCode}" as const
	const offerAddPath = getOfferAddPath ? getOfferAddPath({ porscheCode: porscheCodePlaceholder }) : undefined
	const offerAddPathWithReportSale = getOfferAddPath
		? getOfferAddPath({ porscheCode: porscheCodePlaceholder, reportSale: true })
		: undefined

	const offerAddReturnUrl = offerAddPath
		? new URL(`${dealership}/${locale}${offerAddPath}`, origin).toString()
		: undefined
	const returnUrlReportSale = offerAddPathWithReportSale
		? new URL(`${dealership}/${locale}${offerAddPathWithReportSale}`, origin).toString()
		: undefined

	const searchParams = new URLSearchParams()

	const actions: Action[] = []

	if (canReportSales && returnUrlReportSale !== undefined) {
		actions.push({
			label: "dealer-cta_sale-save-configuration-button",
			url: returnUrlReportSale,
		})
	}

	if (offerAddReturnUrl !== undefined) {
		actions.push({
			label: "dealer-cta_offer-save-configuration-button",
			url: offerAddReturnUrl,
		})
	}

	const [primaryAction, secondaryAction] = actions

	if (primaryAction) {
		searchParams.set("primaryAction[url]", primaryAction.url)
		searchParams.set("primaryAction[label]", primaryAction.label)
	}

	if (secondaryAction) {
		searchParams.set("secondaryAction[url]", secondaryAction.url)
		searchParams.set("secondaryAction[label]", secondaryAction.label)
	}

	if (backActionUrl !== undefined) {
		searchParams.set("backAction[url]", backActionUrl)
		searchParams.set("backAction[label]", "actions_back")
	}

	const queryParamsUrlPart = `?${searchParams.toString()}`

	return `${getConfiguratorHost(environment)}/dealer/${locale}/${dealership}/mode/model-selection${queryParamsUrlPart}`
}
